/* global BigInt */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/api';

/**
 * R43 : WalletContext — multi-wallet Solana sans dépendance lourde.
 *
 * Détecte les wallets injectés (Wallet Standard + extensions classiques) :
 * Phantom, Solflare, Backpack, Exodus, Glow… Tout provider exposant
 * `connect()/disconnect()` + `publicKey` (ou événement `accountChanged`)
 * fonctionne. Signature : `signAndSendTransaction` (standard) ou
 * `signTransaction` (fallback) selon le wallet.
 *
 * « Créer un wallet » : généré côté serveur (POST /marketplace/wallet/create),
 * le secret est affiché UNE fois à l'utilisateur (download JSON) — le
 * serveur ne stocke rien. Le wallet créé n'est pas connecté via provider :
 * il est gardé en session locale (chiffré non — devnet uniquement, avertissement).
 */

const WalletCtx = createContext(null);

/** Détection des providers injectés (Wallet Standard + window.* classiques). */
export function detectWallets() {
  const found = new Map();
  const push = (p, name) => {
    if (p && (p.connect || p.signAndSendTransaction || p.signTransaction) && !found.has(name)) {
      found.set(name, p);
    }
  };
  if (typeof window === 'undefined') return [];
  // Wallet Standard (Phantom, Solflare, Backpack… récents) — signent des octets bruts
  if (window.navigator && Array.isArray(window.navigator.wallets)) {
    window.navigator.wallets.forEach((reg) => {
      try { reg.get().forEach((w) => push({ ...w, standard: true }, w.name || 'Wallet')); } catch { /* noop */ }
    });
  }
  // Fallbacks classiques
  const s = window.solana;
  if (s && s.isPhantom) push(s, 'Phantom');
  if (window.solflare && window.solflare.isSolflare) push(window.solflare, 'Solflare');
  if (window.backpack && window.backpack.isBackpack) push(window.backpack, 'Backpack');
  if (s && s.isExodus) push(s, 'Exodus');
  if (window.glow) push(window.glow, 'Glow');
  return [...found.entries()].map(([name, provider]) => ({
    name,
    provider,
    icon: provider.icon || null,
    readyState: provider.isConnected ? 'connected' : 'installed',
  }));
}

/** Adresse persistée (créé via l'app) — localStorage, devnet only. */
const LS_KEY = 'mkt_wallet_v1';
const loadLocal = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch { return null; } };
const saveLocal = (w) => localStorage.setItem(LS_KEY, JSON.stringify(w));
const clearLocal = () => localStorage.removeItem(LS_KEY);

export function WalletProvider({ children }) {
  const [wallets, setWallets] = useState([]);
  const [connected, setConnected] = useState(null); // { type:'provider'|'created', name, address }
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const unlisten = useRef([]);

  // détection (différée : les extensions s'injectent async)
  useEffect(() => {
    const scan = () => setWallets(detectWallets());
    scan();
    const t = setTimeout(scan, 500);
    window.addEventListener('load', scan);
    return () => { clearTimeout(t); window.removeEventListener('load', scan); };
  }, []);

  // reprise d'un wallet créé localement
  useEffect(() => {
    const l = loadLocal();
    if (l && l.address) setConnected({ type: 'created', name: 'Wallet Elementum', address: l.address });
  }, []);

  const refreshBalance = useCallback(async (address) => {
    try {
      const r = await api.get(`/marketplace/wallet/balance?address=${encodeURIComponent(address)}`);
      setBalance(r.data && r.data.success ? r.data.sol : null);
    } catch { setBalance(null); }
  }, []);

  // R53d : solde SOL mainnet (le "vrai" argent du joueur) + solde ORIN.
  // Le solde devnet reste pollé silencieusement (il déclenche l'auto-refill serveur).
  const [mainnetBalance, setMainnetBalance] = useState(null);
  const [orinBalance, setOrinBalance] = useState(null);
  const refreshMainnet = useCallback(async (address) => {
    try {
      const r = await api.get(`/marketplace/wallet/balance?address=${encodeURIComponent(address)}&cluster=mainnet`);
      setMainnetBalance(r.data && r.data.success ? r.data.sol : null);
    } catch { setMainnetBalance(null); }
  }, []);
  const refreshOrin = useCallback(async () => {
    try {
      const r = await api.get('/orin');
      setOrinBalance(r.data && r.data.success ? r.data.balance : null);
    } catch { setOrinBalance(null); }
  }, []);

  useEffect(() => {
    if (connected) {
      refreshBalance(connected.address);
      refreshMainnet(connected.address);
      refreshOrin();
      const t = setInterval(() => refreshBalance(connected.address), 30000);
      const t2 = setInterval(() => { refreshMainnet(connected.address); refreshOrin(); }, 45000);
      return () => { clearInterval(t); clearInterval(t2); };
    }
    setBalance(null); setMainnetBalance(null); setOrinBalance(null);
  }, [connected, refreshBalance, refreshMainnet, refreshOrin]);

  /** Connecte un wallet injecté (standard ou legacy). */
  const connectProvider = useCallback(async (wallet) => {
    setBusy(true); setError(null);
    try {
      let address = '';
      const p = wallet.provider;
      if (wallet.standard) {
        // Wallet Standard : feature standard:connect + accounts
        const connect = p.features && p.features['standard:connect'];
        if (connect) await connect.connect();
        const acc = p.accounts && p.accounts[0];
        address = acc && (acc.address || (acc.pubkey && acc.pubkey.toString()));
      } else {
        const res = await p.connect();
        address = (res && res.publicKey && res.publicKey.toBase58) ? res.publicKey.toBase58() : String(p.publicKey || '');
      }
      if (!address) throw new Error('Wallet n\u2019a pas renvoyé d\u2019adresse.');
      clearLocal();
      setConnected({ type: 'provider', name: wallet.name, address, standard: !!wallet.standard });
      // suivi des changements de compte
      if (p.on) {
        const h = () => wallet.provider.disconnect().catch(() => {});
        p.on('disconnect', h);
        unlisten.current.push(() => p.off && p.off('disconnect', h));
      }
    } catch (e) {
      setError(e.message || 'Connexion refusée.');
    } finally { setBusy(false); }
  }, []);

  /** Création d'un wallet (serveur) — retourne le secret à afficher UNE fois. */
  const createWallet = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post('/marketplace/wallet/create');
      const { publicKey, secret } = r.data;
      saveLocal({ address: publicKey, secret }); // devnet only, local
      setConnected({ type: 'created', name: 'Wallet Elementum', address: publicKey });
      return { publicKey, secret };
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      return null;
    } finally { setBusy(false); }
  }, []);

  /** Import d'un wallet créé précédemment (secret JSON). */
  const importWallet = useCallback((publicKey) => {
    saveLocal({ address: publicKey, secret: null });
    setConnected({ type: 'created', name: 'Wallet Elementum', address: publicKey });
  }, []);

  /* R53c : bouton faucet manuel supprimé — le serveur airdrop 0.02 SOL
     automatiquement aux nouveaux joueurs et re-top-up sous 0.001 SOL. */

  /** Secret du wallet Elementum local (devnet) — pour signature serveur. */
  const getLocalSecret = useCallback(() => {
    const l = loadLocal();
    return l && l.secret ? l.secret : null;
  }, []);

  /**
   * R44 : signe + envoie une transaction sérialisée (base64).
   * - wallet injecté : Wallet Standard `solana:signAndSendTransaction`
   * - wallet Elementum local : le caller passe getLocalSecret() à l'API
   * Retourne la signature base58 (jamais hex — le serveur valide du base58).
   */
  const toBase58 = (bytes) => {
    let n = 0n;
    for (const b of bytes) n = n * 256n + BigInt(b);
    const A = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
    let s = '';
    while (n > 0n) { s = A[Number(n % 58n)] + s; n /= 58n; }
    for (const b of bytes) { if (b === 0) s = '1' + s; else break; }
    return s;
  };
  const signAndSend = useCallback(async (txBase64, chain = 'solana:devnet') => {
    if (!connected) throw new Error('Wallet non connecté.');
    const w = wallets.find((x) => x.name === connected.name);
    const p = w && w.provider;
    const bytes = Uint8Array.from(atob(txBase64), (ch) => ch.charCodeAt(0));
    if (p && p.features && p.features['solana:signAndSendTransaction']) {
      const r = await p.features['solana:signAndSendTransaction']
        .signAndSendTransaction({ transaction: bytes, chain });
      const sig = r && (r.signature || (r[0] && r[0].signature));
      if (sig instanceof Uint8Array) return toBase58(sig);
      return String(sig); // déjà base58 pour la plupart des wallets
    }
    // Legacy Phantom/Solflare (récents) : acceptent aussi la forme (chain, input)
    if (p && typeof p.signAndSendTransaction === 'function' && p.signAndSendTransaction.length >= 2) {
      const r = await p.signAndSendTransaction(chain, { transaction: bytes });
      return String(r && (r.signature || r.signatureBase58 || r));
    }
    throw new Error('Ce wallet ne supporte pas la signature de transactions. Utilisez un wallet à jour (Phantom, Solflare, Backpack) ou un wallet Elementum local.');
  }, [connected, wallets]);

  const disconnect = useCallback(() => {
    unlisten.current.forEach((f) => { try { f(); } catch { /* noop */ } });
    unlisten.current = [];
    const prov = wallets.find((w) => w.name === (connected && connected.name));
    if (prov && prov.provider.disconnect) prov.provider.disconnect().catch(() => {});
    clearLocal();
    setConnected(null);
    setBalance(null);
  }, [wallets, connected]);

  return (
    <WalletCtx.Provider value={{
      wallets, connected, balance, busy, error,
      connectProvider, createWallet, importWallet, disconnect, refreshBalance, refreshMainnet,
      mainnetBalance, orinBalance,
      getLocalSecret, signAndSend,
    }}>
      {children}
    </WalletCtx.Provider>
  );
}

export const useWallet = () => useContext(WalletCtx);
export default WalletCtx;
